import { lookupBotIdentity, type BotIdentity } from "./botIdentity";

export type WorkflowNodeType =
  "produce" | "review" | "release" | "comms" | "support" | "archive";

export type WorkflowBotRole =
  "dri" | "blind_review" | "tool" | "master" | "support";

export type WorkflowSource = "fallback" | "db";

export type WorkflowBot = {
  agent_id: string;
  role_name: string;
  bot_role: WorkflowBotRole;
  required: boolean;
  sort_order: number;
  active: boolean;
  identity: BotIdentity | null;
};

export type WorkflowNode = {
  id?: number;
  node_key: string;
  label: string;
  sort_order: number;
  node_type: WorkflowNodeType;
  is_blind_review: boolean;
  bots: WorkflowBot[];
};

export type WorkflowTemplate = {
  id?: number;
  legend_token: string;
  line_key: string;
  key: string;
  name: string;
  version: number;
  is_default: boolean;
  description: string | null;
  nodes: WorkflowNode[];
  source: WorkflowSource;
};

export type WorkflowRun = {
  id: number;
  workflow_id: number;
  legend_token: string;
  current_node_key: string;
  title: string;
  status: string;
};

const TOPIC = "f0a68444-1c3c-4373-8f6c-7b512439da3f";
const FILM = "6d6fa75c-63ef-48b3-a239-ad6ed78cf3b7";
const VO_REVIEW = "4294154e-5175-4fa4-8c8e-2dfc6c9790de";
const DENS_REVIEW = "be762a93-2b60-41a8-a779-f0fdf2acaa1e";
const GATE_REVIEW = "2564021c-a781-44b6-a9fb-08db5265b281";
const RELEASE = "62333102-58c8-4251-b45e-3e8848b9b639";
const MASTER = "d9815ba8-5c5d-4bd4-b882-35ce89fd139a";

function bot(
  agent_id: string,
  role_name: string,
  bot_role: WorkflowBotRole,
): WorkflowBot {
  return {
    agent_id,
    role_name,
    bot_role,
    required: true,
    sort_order: 0,
    active: true,
    identity: lookupBotIdentity(agent_id, null),
  };
}

function node(
  node_key: string,
  label: string,
  sort_order: number,
  node_type: WorkflowNodeType,
  bots: WorkflowBot[] = [],
  is_blind_review = false,
): WorkflowNode {
  return {
    node_key,
    label,
    sort_order,
    node_type,
    is_blind_review,
    bots,
  };
}

function template(
  legend_token: string,
  line_key: string,
  key: string,
  name: string,
  description: string,
  nodes: WorkflowNode[],
): WorkflowTemplate {
  return {
    legend_token,
    line_key,
    key,
    name,
    version: 1,
    is_default: true,
    description,
    nodes,
    source: "fallback",
  };
}

function brief(
  token: string,
  line: string,
  key: string,
  name: string,
): WorkflowTemplate {
  return template(token, line, key, name, "简版：文案 → 发布。", [
    node("copy", "文案", 1, "produce"),
    node("publish", "发布", 2, "release"),
  ]);
}

function empty(
  token: string,
  line: string,
  key: string,
  name: string,
  description: string,
): WorkflowTemplate {
  return template(token, line, key, name, description, []);
}

/** Static package used when the workflow tables cannot be read. No runs. */
export const FALLBACK_WORKFLOWS: WorkflowTemplate[] = [
  template(
    "biz.guanxin",
    "gx",
    "guanxin.default_gate",
    "观心微语 · 默认闸门包流水",
    "结构模板。不插入期次或日格 run。",
    [
      node("topic", "选题", 1, "produce", [bot(TOPIC, "观心微语·选题", "dri")]),
      node("vo", "口播", 2, "produce", [bot(FILM, "观心微语·整片", "dri")]),
      node(
        "vo_review",
        "口播审",
        3,
        "review",
        [bot(VO_REVIEW, "观心微语·口播审", "blind_review")],
        true,
      ),
      node("make_text", "制作文本", 4, "produce", [
        bot(FILM, "观心微语·整片", "dri"),
      ]),
      node("stills", "静帧", 5, "produce", [bot(FILM, "观心微语·整片", "dri")]),
      node(
        "dens_review",
        "密度审",
        6,
        "review",
        [bot(DENS_REVIEW, "观心微语·密度审", "blind_review")],
        true,
      ),
      node("film", "成片", 7, "produce", [bot(FILM, "观心微语·整片", "dri")]),
      node(
        "gate_review",
        "闸门审",
        8,
        "review",
        [bot(GATE_REVIEW, "观心微语·闸门审", "blind_review")],
        true,
      ),
      node("release_pack", "发布包", 9, "release", [
        bot(RELEASE, "观心微语·发布包", "dri"),
      ]),
      node("master_archive", "Master落盘", 10, "archive", [
        bot(MASTER, "个人IP·Master", "master"),
      ]),
      node("pending_publish", "待发", 11, "release", [
        bot(RELEASE, "观心微语·发布包", "support"),
      ]),
    ],
  ),
  template(
    "biz.caogao",
    "cg",
    "caogao.copy_gate",
    "小凡的草稿本 · 文案到闸门",
    "简版：文案 → 闸门审。",
    [
      node("copy", "文案", 1, "produce"),
      node("gate_review", "闸门审", 2, "review", [], true),
    ],
  ),
  template(
    "biz.dushubiji",
    "xyz",
    "dushubiji.copy_make",
    "小凡的读书笔记 · 文案到制作",
    "简版：文案 → 制作。",
    [node("copy", "文案", 1, "produce"), node("make", "制作", 2, "produce")],
  ),
  brief("biz.cosytab", "cosy", "cosytab.brief", "CosyTab · 简版"),
  brief("biz.geo", "geo", "geo.brief", "GEO · 简版"),
  brief("biz.tuwen", "tw", "tuwen.brief", "小凡的图文故事 · 简版"),
  empty(
    "biz.niuma",
    "nm",
    "niuma.empty",
    "打工牛马连连看 · 空模板",
    "空模板，不开产。",
  ),
  empty(
    "idea.tongxin",
    "idea",
    "tongxin.empty",
    "童心会话视频 · 空模板",
    "想法线空模板，不开产。",
  ),
  empty("matter.jiaoliu", "you", "jiaoliu.empty", "交流 · 空模板", "空模板。"),
  empty("matter.gongsi", "co", "gongsi.empty", "公司事项 · 空模板", "空模板。"),
  empty("matter.morun", "soft", "morun.empty", "默跑 · 空模板", "空模板。"),
];
